import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { noAccessReason, useIsRightLocked, usePermission } from '../../auth'

/**
 * Right (view) required to open each dashboard tab. Tabs not listed (e.g. the
 * static Strat Plan KPI's tab) need no right.
 */
const DASHBOARD_TAB_RIGHTS: Record<string, string> = {
  dashboard: 'pipeline_status_dashboard',
  stagegates: 'stage_gate_dashboard',
  evaluation: 'panoptic_evaluation_dashboard',
  projects: 'stage_gate_dashboard',
  prospectus: 'investor_prospectus_dashboard',
  reports: 'reports',
  settings: 'user_management',
}

/** Dashboard tabs in menu order; the first one the user may open is the landing tab. */
const DASHBOARD_TAB_ORDER = ['dashboard', 'stagegates', 'evaluation', 'projects', 'prospectus', 'reports', 'kpi_monitoring', 'settings']

/** Returns whether the current user may open a dashboard tab. */
export const useCanOpenDashboardTab = () => {
  const { can } = usePermission()

  return useCallback(
    (tab: string) => !DASHBOARD_TAB_RIGHTS[tab] || can(DASHBOARD_TAB_RIGHTS[tab], 'view'),
    [can]
  )
}

/** Returns whether the signed-in user's role cannot open a dashboard tab, so links into it can be disabled. */
export const useIsDashboardTabLocked = () => {
  const isRightLocked = useIsRightLocked()

  return useCallback((tab: string) => Boolean(DASHBOARD_TAB_RIGHTS[tab]) && isRightLocked(DASHBOARD_TAB_RIGHTS[tab]), [isRightLocked])
}

/** Enter-button props for a link into a dashboard tab: disabled, with the reason, when the role cannot open it. */
export const useDashboardTabEntry = (tab: string, moduleName: string) => {
  const navigate = useNavigate()
  const isTabLocked = useIsDashboardTabLocked()

  return {
    onButtonClick: () => navigate(`/dashboard/${tab}`),
    disabledReason: isTabLocked(tab) ? noAccessReason(moduleName) : undefined,
  }
}

/** First tab the current user may open (Strat Plan KPI's needs no right, so there is always one). */
export const useDefaultDashboardTab = (): string => {
  const canOpenTab = useCanOpenDashboardTab()

  return DASHBOARD_TAB_ORDER.find(canOpenTab) ?? 'kpi_monitoring'
}
